<script setup lang="ts">
import {
  computed,
  ref,
  onMounted,
  type Ref,
  nextTick,
  onBeforeUnmount,
  watch,
} from 'vue';
import {
  RBtn,
  useLoadingSpinnerStore,
} from '@therobotcompany/robco-frontend-library';
import { Bar } from 'vue-chartjs';
import {
  type Chart as ChartType,
  type ChartData,
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  LinearScale,
  CategoryScale,
  TimeScale,
} from 'chart.js';
import { subDays, parseISO } from 'date-fns';
import { useRoute, useRouter } from 'vue-router';
import { useDisplay } from 'vuetify';
import DatePickerComponent from '@/components/core/DatePickerComponent.vue';
import { loadRobotsSerialNumbers } from '@/api/RobotsApi';
import {
  loadTimeMetricsStream,
  loadCountMetricsStream,
  exportMetricsData,
  type ExportFileType,
} from '@/api/OeeMetricsApi';
import { requiredRule, serialNumberRegexRule } from '@/helpers/FormRules';
import 'chartjs-adapter-date-fns';
import gettext from '@/i18n/gettext';
import {
  timeColorMap,
  countColorMap,
  timeLabelDisplayMap,
  countLabelDisplayMap,
} from '@/models/OeeMetrics';
import {
  formatTimeMetric,
  getTimeChartOptions,
  getCountChartOptions,
  getEmptyChartData,
  updateChartDatasets,
} from '@/helpers/OeeMetricsHelper';
import loadRobotRobFlowVariables from '@/api/RobFlowApi';
import type RobFlowVariables from '@/models/RobFlow';
import formatDate from '@/helpers/DateFormatterHelper';

const { $gettext } = gettext;

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  BarElement,
  LinearScale,
  CategoryScale,
  TimeScale,
);

const route = useRoute();
const router = useRouter();
const { mdAndUp } = useDisplay();

const loadingSpinnerStore = useLoadingSpinnerStore();
const serialNumbers: Ref<string[]> = ref([]);
const targetCu: Ref<string> = ref('');
const dateRange: Ref<[Date, Date] | null> = ref([
  subDays(new Date(), 30),
  new Date(),
]);
const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
const targetCount: Ref<number> = ref(0);
const targetCountUnit: Ref<string> = ref('hour');
const targetCountUnits = computed(() => [
  { title: $gettext('per minute'), value: 'minute' },
  { title: $gettext('per hour'), value: 'hour' },
  { title: $gettext('per day'), value: 'day' },
  { title: $gettext('per week'), value: 'week' },
  { title: $gettext('per month'), value: 'month' },
]);
const isStreaming = ref(false);
const timeBarChart: Ref<{ chart: ChartType | undefined } | null> = ref(null);
const countBarChart: Ref<{ chart: ChartType | undefined } | null> = ref(null);
const selectedTimeMetrics: Ref<Set<string>> = ref(new Set<string>());
const selectedCountMetrics: Ref<Set<string>> = ref(new Set<string>());
const isTimeMetricSelected = (key: string) =>
  selectedTimeMetrics.value.has(key);

const timeChartData: Ref<ChartData<'bar'>> = ref(getEmptyChartData());
const countChartData: Ref<ChartData<'bar'>> = ref(getEmptyChartData());

const timeValues: Ref<Record<string, number>> = ref({
  planned_downtime: 0,
  unplanned_downtime: 0,
  idle: 0,
  starved: 0,
  blocked: 0,
  producing: 0,
  no_data: 0, // Excluded from OEE: Spans with no telemetry events
});

const countValues: Ref<Record<string, number>> = ref({
  good_count: 0,
  bad_count: 0,
  fault: 0,
  failure: 0,
});

const timeChartOptions = getTimeChartOptions();
const countChartOptions = getCountChartOptions();

const BATCH_UPDATE_INTERVAL_MS = 500;
const FOLLOW_MODE_UPDATE_INTERVAL_MS = 300000; // 5 minutes
let timeUpdateTimeout: number | null = null;
let countUpdateTimeout: number | null = null;

const errorState: Ref<{ message: string; type: 'error' | 'warning' | null }> =
  ref({ message: '', type: null });

const isFollowingCurrentState: Ref<boolean> = ref(false);
let updateInterval: number | null = null;

const showHelpDialog = ref(false);
const helpDialogTab = ref(0);

// RobFlow Variables state
const robflowVariables: Ref<RobFlowVariables | null> = ref(null);
const robflowHeaders = [
  { title: $gettext('Variable'), value: 'key', sortable: true },
  { title: $gettext('Value'), value: 'value', sortable: true },
];
const robflowTableData = computed(() => {
  if (!robflowVariables.value) {
    return [] as Array<{ key: string; value: string | number | boolean }>;
  }
  return Object.entries(robflowVariables.value.variables).map(
    ([key, value]) => ({ key, value }),
  );
});

const metricsHelpData = computed(() => {
  return {
    timeMetrics: [
      {
        name: $gettext('Idle'),
        description: $gettext('Time when the system is not actively producing'),
        trigger: $gettext(
          'Automatic when:<br/>&nbsp;&nbsp;• Flow ends<br/>&nbsp;&nbsp;• Flow is paused or stopped<br/>&nbsp;&nbsp;• Flow is in debug or testing mode',
        ),
      },
      {
        name: $gettext('Blocked'),
        description: $gettext(
          'Time when the system cannot produce due to downstream constraints',
        ),
        trigger: $gettext(
          'Manual in the Flow, requires start and end blocked nodes. The blocked event is triggered on the start blocked Node.',
        ),
      },
      {
        name: $gettext('Starved'),
        description: $gettext(
          'Time when the system cannot produce due to upstream constraints',
        ),
        trigger: $gettext(
          'Manual in the Flow, requires start and end starved Nodes. The starved event is triggered on the start starved Node.',
        ),
      },
      {
        name: $gettext('Producing'),
        description: $gettext('Time when the system is actively producing'),
        trigger: $gettext(
          'Automatic when:<br/>&nbsp;&nbsp;• Flow starts<br/>&nbsp;&nbsp;• Flow gets to End Blocked Node<br/>&nbsp;&nbsp;• Flow gets to End Starved Node',
        ),
      },
      {
        name: $gettext('Planned Downtime'),
        description: $gettext(
          'Scheduled time when the system is intentionally not producing',
        ),
        trigger: $gettext('Manually set by the user in RobCo Studio'),
      },
      {
        name: $gettext('Unplanned Downtime'),
        description: $gettext(
          'Unscheduled time when the system is not producing due to issues',
        ),
        trigger: $gettext(
          'Automatic when an error occurs and the Flow stops. Works together with the Failure count.',
        ),
      },
      {
        name: $gettext('No Data'),
        description: $gettext(
          'Time when there are no data samples reported. This time is excluded from the OEE calculations.',
        ),
        trigger: $gettext('Automatic when there is no data reported.'),
      },
    ],
    countMetrics: [
      {
        name: $gettext('Good Count'),
        description: $gettext('Number of successfully produced items'),
        trigger: $gettext(
          'Automatically triggered by attaching an event to a Variable. Requires a checkbox for an Integer Variable specifying good count. Every time the value changes, the event is emitted with the Variable delta.',
        ),
      },
      {
        name: $gettext('Bad Count'),
        description: $gettext('Number of items that failed quality checks'),
        trigger: $gettext(
          'Automatically triggered by attaching an event to a Variable. Requires a checkbox for an Integer Variable specifying bad count. Every time the value changes, the event is emitted with the Variable delta.',
        ),
      },
      {
        name: $gettext('Fault'),
        description: $gettext(
          "Number of minor issues that didn't stop production",
        ),
        trigger: $gettext(
          'Automatic when encountering an error but Flow continues running.',
        ),
      },
      {
        name: $gettext('Failure'),
        description: $gettext('Number of major issues that stopped production'),
        trigger: $gettext(
          'Automatic when encountering an error but Flow stops.',
        ),
      },
    ],
    summaryMetrics: [
      {
        name: $gettext('OEE'),
        description: $gettext('Overall Equipment Effectiveness'),
        calculation: $gettext('Availability × Performance × Quality'),
      },
      {
        name: $gettext('Availability'),
        description: $gettext(
          'Ratio of actual production time to Planned Production Time',
        ),
        calculation: $gettext('Uptime / (Uptime + Downtime)'),
      },
      {
        name: $gettext('System Availability'),
        description: $gettext(
          'Ratio of actual production time to total time excluding planned downtime',
        ),
        calculation: $gettext('Uptime / (Uptime + Unplanned Downtime)'),
      },
      {
        name: $gettext('Performance'),
        description: $gettext(
          'Ratio of actual production rate to Target Production Rate during Uptime',
        ),
        calculation: $gettext('Total Count / Target Count'),
      },
      {
        name: $gettext('Quality'),
        description: $gettext('Ratio of good parts to total parts produced'),
        calculation: $gettext('Good Count / (Good Count + Bad Count)'),
      },
      {
        name: $gettext('Uptime'),
        description: $gettext('Total time the system is operational'),
        calculation: $gettext(
          'Producing Time + Idle Time + Blocked Time + Starved Time',
        ),
      },
      {
        name: $gettext('Downtime'),
        description: $gettext('Total time the system is not operational'),
        calculation: $gettext('Planned Downtime + Unplanned Downtime'),
      },
      {
        name: $gettext('Planned Production Time'),
        description: $gettext('Total time available for production'),
        calculation: $gettext('Uptime + Downtime'),
      },
      {
        name: $gettext('MTBF'),
        description: $gettext('Mean Time Between Failures'),
        calculation: $gettext('Uptime / Number of Failures'),
      },
      {
        name: $gettext('MTTR'),
        description: $gettext('Mean Time To Repair'),
        calculation: $gettext('Unplanned Downtime / Number of Failures'),
      },
      {
        name: $gettext('Process Cycle Time'),
        description: $gettext('Average time to complete one production cycle'),
        calculation: $gettext('Producing Time / Total Count'),
      },
      {
        name: $gettext('Process Constraint Level'),
        description: $gettext('Ratio of time spent producing to total uptime'),
        calculation: $gettext('Producing Time / Uptime'),
      },
      {
        name: $gettext('Machine Utilization'),
        description: $gettext(
          'Ratio of time spent producing to planned production time',
        ),
        calculation: $gettext('Producing Time / Planned Production Time'),
      },
      {
        name: $gettext('Equipment Robustness'),
        description: $gettext(
          'Measure of how well the equipment maintains performance over time',
        ),
        calculation: $gettext('Producing Time / Number of Failures'),
      },
    ],
  };
});

const clearError = () => {
  errorState.value = { message: '', type: null };
};

const handleDataParseError = (
  line: string,
  error: unknown,
  source: 'time' | 'count',
) => {
  console.warn(`Error parsing ${source} metrics line:`, line, error);
  errorState.value = {
    message: $gettext(
      'Some data points could not be processed. Please check your connection.',
    ),
    type: 'warning',
  };
};

timeChartOptions.onClick = (event, elements) => {
  if (!elements || elements.length === 0) {
    return;
  }
  const { datasetIndex } = elements[0];
  if (datasetIndex === undefined) {
    return;
  }

  const chart = timeBarChart.value?.chart;
  if (!chart) {
    return;
  }

  const key = Object.keys(timeColorMap)[datasetIndex];
  if (!key) {
    return;
  }

  selectedTimeMetrics.value.clear();
  selectedTimeMetrics.value.add(key);

  chart.data.datasets.forEach((ds, i) => {
    ds.hidden = i !== datasetIndex;
  });
  chart.update();
};

countChartOptions.onClick = (event, elements) => {
  if (!elements || elements.length === 0) {
    return;
  }
  const { datasetIndex } = elements[0];
  if (datasetIndex === undefined) {
    return;
  }

  const chart = countBarChart.value?.chart;
  if (!chart) {
    return;
  }

  const key = Object.keys(countColorMap)[datasetIndex];
  if (!key) {
    return;
  }

  selectedCountMetrics.value.clear();
  selectedCountMetrics.value.add(key);

  chart.data.datasets.forEach((ds, i) => {
    ds.hidden = i !== datasetIndex;
  });
  chart.update();
};

const showOnlyTimeDataset = (index: number) => {
  const chart = timeBarChart.value?.chart;
  if (!chart) {
    return;
  }

  const key = Object.keys(timeColorMap)[index];
  if (!key) {
    return;
  }

  if (selectedTimeMetrics.value.has(key)) {
    selectedTimeMetrics.value.delete(key);
  } else {
    selectedTimeMetrics.value.add(key);
  }

  if (selectedTimeMetrics.value.size === 0) {
    chart.data.datasets.forEach((ds) => {
      ds.hidden = false;
    });
  } else {
    chart.data.datasets.forEach((ds, i) => {
      const datasetKey = Object.keys(timeColorMap)[i];
      ds.hidden = !selectedTimeMetrics.value.has(datasetKey);
    });
  }

  chart.update();
};

const showOnlyCountDataset = (index: number) => {
  const chart = countBarChart.value?.chart;
  if (!chart) {
    return;
  }

  const key = Object.keys(countColorMap)[index];
  if (!key) {
    return;
  }

  if (selectedCountMetrics.value.has(key)) {
    selectedCountMetrics.value.delete(key);
  } else {
    selectedCountMetrics.value.add(key);
  }

  if (selectedCountMetrics.value.size === 0) {
    chart.data.datasets.forEach((ds) => {
      ds.hidden = false;
    });
  } else {
    chart.data.datasets.forEach((ds, i) => {
      const datasetKey = Object.keys(countColorMap)[i];
      ds.hidden = !selectedCountMetrics.value.has(datasetKey);
    });
  }
  chart.update();
};

const resetCharts = () => {
  const timeChart = timeBarChart.value?.chart;
  const countChart = countBarChart.value?.chart;

  if (timeChart) {
    selectedTimeMetrics.value.clear();
    timeChart.data.datasets.forEach((dataset) => {
      dataset.hidden = false;
    });
    timeChart.update();
  }

  if (countChart) {
    selectedCountMetrics.value.clear();
    countChart.data.datasets.forEach((dataset) => {
      dataset.hidden = false;
    });
    countChart.update();
  }
};

async function fetchUserRobotsSerialNumbers() {
  loadingSpinnerStore.setShowLoadingOverlay(true);
  try {
    serialNumbers.value = await loadRobotsSerialNumbers();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    console.error(err);
  } finally {
    loadingSpinnerStore.setShowLoadingOverlay(false);
  }
}

const getTargetCountForPeriod = computed(() => {
  if (!targetCount.value || !dateRange.value) {
    return 0;
  }
  const [from, to] = dateRange.value;
  const hoursInPeriod = (to.getTime() - from.getTime()) / (1000 * 60 * 60);

  switch (targetCountUnit.value) {
    case 'minute':
      return targetCount.value * 60 * hoursInPeriod;
    case 'hour':
      return targetCount.value * hoursInPeriod;
    case 'day':
      return targetCount.value * (hoursInPeriod / 24);
    case 'week':
      return targetCount.value * (hoursInPeriod / (24 * 7));
    case 'month':
      return targetCount.value * (hoursInPeriod / (24 * 30));
    default:
      return targetCount.value * hoursInPeriod;
  }
});

const quality = computed(() => {
  const totalCount = countValues.value.good_count + countValues.value.bad_count;
  return totalCount > 0 ? countValues.value.good_count / totalCount : 0;
});

const uptime = computed(() => {
  const totalUptime =
    timeValues.value.producing +
    timeValues.value.idle +
    timeValues.value.blocked +
    timeValues.value.starved;
  return totalUptime > 0 ? totalUptime : 0;
});

const downtime = computed(() => {
  const totalDowntime =
    timeValues.value.planned_downtime + timeValues.value.unplanned_downtime;
  return totalDowntime > 0 ? totalDowntime : 0;
});

const performance = computed(() => {
  const totalCount = countValues.value.good_count + countValues.value.bad_count;
  const totalTime = uptime.value + downtime.value;
  const availabilityFactor = totalTime > 0 ? uptime.value / totalTime : 0;
  const uptimeAdjustedTargetCount =
    getTargetCountForPeriod.value * availabilityFactor;

  return uptimeAdjustedTargetCount > 0
    ? totalCount / uptimeAdjustedTargetCount
    : 0;
});

const mtbf = computed(() => {
  return countValues.value.failure > 0
    ? uptime.value / countValues.value.failure
    : 0;
});

const mttr = computed(() => {
  return countValues.value.failure > 0
    ? timeValues.value.unplanned_downtime / countValues.value.failure
    : 0;
});

const processCycleTime = computed(() => {
  const totalCount = countValues.value.good_count + countValues.value.bad_count;
  return totalCount > 0 ? timeValues.value.producing / totalCount : 0;
});

const availability = computed(() => {
  const totalTime = uptime.value + downtime.value;
  return totalTime > 0 ? uptime.value / totalTime : 0;
});

const systemAvailability = computed(() => {
  const totalTime = uptime.value + timeValues.value.unplanned_downtime;
  return totalTime > 0 ? uptime.value / totalTime : 0;
});

const plannedProductionTime = computed(() => {
  const totalTime = uptime.value + downtime.value;
  return totalTime > 0 ? totalTime : 0;
});

const processConstraintLevel = computed(() => {
  return uptime.value > 0 ? timeValues.value.producing / uptime.value : 0;
});

const machineUtilization = computed(() => {
  return plannedProductionTime.value > 0
    ? timeValues.value.producing / plannedProductionTime.value
    : 0;
});

const oee = computed(() => {
  return availability.value * performance.value * quality.value;
});

const equipmentRobustness = computed(() => {
  return countValues.value.failure > 0
    ? timeValues.value.producing / countValues.value.failure
    : 0;
});

const SUMMARY_METRIC_KEYS = {
  UPTIME: 'uptime',
  DOWNTIME: 'downtime',
  AVAILABILITY: 'availability',
  SYSTEM_AVAILABILITY: 'system_availability',
  PLANNED_PRODUCTION_TIME: 'planned_production_time',
  PERFORMANCE: 'performance',
  QUALITY: 'quality',
  MTBF: 'mtbf',
  MTTR: 'mttr',
  PROCESS_CYCLE_TIME: 'process_cycle_time',
  PROCESS_CONSTRAINT_LEVEL: 'process_constraint_level',
  MACHINE_UTILIZATION: 'machine_utilization',
  EQUIPMENT_ROBUSTNESS: 'equipment_robustness',
  OEE: 'oee',
} as const;

const summaryMetrics = computed(() => [
  {
    key: SUMMARY_METRIC_KEYS.UPTIME,
    label: $gettext('Uptime'),
    value: `${formatTimeMetric(uptime.value)}`,
  },
  {
    key: SUMMARY_METRIC_KEYS.DOWNTIME,
    label: $gettext('Downtime'),
    value: `${formatTimeMetric(downtime.value)}`,
  },
  {
    key: SUMMARY_METRIC_KEYS.AVAILABILITY,
    label: $gettext('Availability'),
    value: `${(availability.value * 100).toFixed(2)}%`,
  },
  {
    key: SUMMARY_METRIC_KEYS.SYSTEM_AVAILABILITY,
    label: $gettext('System Availability'),
    value: `${(systemAvailability.value * 100).toFixed(2)}%`,
  },
  {
    key: SUMMARY_METRIC_KEYS.PLANNED_PRODUCTION_TIME,
    label: $gettext('Planned Production Time'),
    value: `${formatTimeMetric(plannedProductionTime.value)}`,
  },
  {
    key: SUMMARY_METRIC_KEYS.PERFORMANCE,
    label: $gettext('Performance'),
    value: `${(performance.value * 100).toFixed(2)}%`,
  },
  {
    key: SUMMARY_METRIC_KEYS.QUALITY,
    label: $gettext('Quality'),
    value: `${(quality.value * 100).toFixed(2)}%`,
  },
  {
    key: SUMMARY_METRIC_KEYS.MTBF,
    label: $gettext('MTBF'),
    value: `${formatTimeMetric(mtbf.value)}`,
  },
  {
    key: SUMMARY_METRIC_KEYS.MTTR,
    label: $gettext('MTTR'),
    value: `${formatTimeMetric(mttr.value)}`,
  },
  {
    key: SUMMARY_METRIC_KEYS.PROCESS_CYCLE_TIME,
    label: $gettext('Process Cycle Time'),
    value: `${formatTimeMetric(processCycleTime.value)}`,
  },
  {
    key: SUMMARY_METRIC_KEYS.PROCESS_CONSTRAINT_LEVEL,
    label: $gettext('Process Constraint Level'),
    value: `${(processConstraintLevel.value * 100).toFixed(2)}%`,
  },
  {
    key: SUMMARY_METRIC_KEYS.MACHINE_UTILIZATION,
    label: $gettext('Machine Utilization'),
    value: `${(machineUtilization.value * 100).toFixed(2)}%`,
  },
  {
    key: SUMMARY_METRIC_KEYS.EQUIPMENT_ROBUSTNESS,
    label: $gettext('Equipment Robustness'),
    value: `${formatTimeMetric(equipmentRobustness.value)}`,
  },
]);

const updateTimeDataset = (timeDatasetMap: Record<string, number[]>) => {
  timeChartData.value = {
    labels: timeChartData.value.labels,
    datasets: updateChartDatasets(
      timeDatasetMap,
      timeColorMap,
      timeLabelDisplayMap.value,
      { stack: 'total' },
    ),
  };
};

const updateCountDataset = (countDatasetMap: Record<string, number[]>) => {
  countChartData.value = {
    labels: countChartData.value.labels,
    datasets: updateChartDatasets(
      countDatasetMap,
      countColorMap,
      countLabelDisplayMap.value,
      { border: true },
    ),
  };
};

const batchedUpdateTimeDataset = (timeDatasetMap: Record<string, number[]>) => {
  if (timeUpdateTimeout) {
    clearTimeout(timeUpdateTimeout);
  }

  timeUpdateTimeout = window.setTimeout(() => {
    updateTimeDataset(timeDatasetMap);
    nextTick(() => {
      if (timeBarChart.value?.chart) {
        timeBarChart.value.chart.update('none');
      }
    });
  }, BATCH_UPDATE_INTERVAL_MS);
};

const batchedUpdateCountDataset = (
  countDatasetMap: Record<string, number[]>,
) => {
  if (countUpdateTimeout) {
    clearTimeout(countUpdateTimeout);
  }

  countUpdateTimeout = window.setTimeout(() => {
    updateCountDataset(countDatasetMap);
    nextTick(() => {
      if (countBarChart.value?.chart) {
        countBarChart.value.chart.update('none');
      }
    });
  }, BATCH_UPDATE_INTERVAL_MS);
};

async function streamTimeMetrics(
  fromEpoch: number,
  toEpoch: number,
  datasetMap: Record<string, number[]>,
) {
  const response = await loadTimeMetricsStream(
    targetCu.value,
    fromEpoch,
    toEpoch,
    timezone,
  );
  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('No response body stream for time metrics');
  }

  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  let done = false;

  while (!done) {
    const result = await reader.read();
    ({ done } = result);

    if (result.value) {
      buffer += decoder.decode(result.value, { stream: true });
      const parts = buffer.split('\n');
      buffer = parts.pop() || '';

      parts.forEach((line) => {
        if (!line.trim()) {
          return;
        }
        try {
          const record = JSON.parse(line);
          const recordDate = new Date(record.day);
          recordDate.setHours(0, 0, 0, 0);

          const index = (timeChartData.value.labels as Date[]).findIndex(
            (d) => d.getTime() === recordDate.getTime(),
          );
          if (index === -1) {
            return;
          }

          Object.entries(record.agg_times).forEach(([eventType, seconds]) => {
            if (!datasetMap[eventType]) {
              return;
            }
            datasetMap[eventType][index] = Number(seconds);
            timeValues.value[eventType] += Number(seconds);
          });

          batchedUpdateTimeDataset(datasetMap);
        } catch (err) {
          handleDataParseError(line, err, 'time');
        }
      });
    }
  }
}

async function streamCountMetrics(
  fromEpoch: number,
  toEpoch: number,
  datasetMap: Record<string, number[]>,
) {
  const response = await loadCountMetricsStream(
    targetCu.value,
    fromEpoch,
    toEpoch,
    timezone,
  );
  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('No response body stream for count metrics');
  }

  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  let done = false;

  while (!done) {
    const result = await reader.read();
    ({ done } = result);

    if (result.value) {
      buffer += decoder.decode(result.value, { stream: true });
      const parts = buffer.split('\n');
      buffer = parts.pop() || '';

      parts.forEach((line) => {
        if (!line.trim()) {
          return;
        }
        try {
          const record = JSON.parse(line);
          const recordDate = new Date(record.day);
          recordDate.setHours(0, 0, 0, 0);

          const index = (countChartData.value.labels as Date[]).findIndex(
            (d) => d.getTime() === recordDate.getTime(),
          );
          if (index === -1) {
            return;
          }

          Object.entries(record.agg_counts).forEach(([eventType, count]) => {
            datasetMap[eventType][index] = Number(count);
            countValues.value[eventType] =
              (countValues.value[eventType] || 0) + Number(count);
          });

          batchedUpdateCountDataset(datasetMap);
        } catch (err) {
          handleDataParseError(line, err, 'count');
        }
      });
    }
  }
}

async function onQueryChange() {
  if (!targetCu.value || !dateRange.value) {
    return;
  }

  const [from, to] = dateRange.value;
  if (!from || !to) {
    return;
  }

  clearError();

  // Reset all data
  isStreaming.value = true;

  // Reset all values
  Object.keys(timeValues.value).forEach((key) => {
    timeValues.value[key] = 0;
  });

  Object.keys(countValues.value).forEach((key) => {
    countValues.value[key] = 0;
  });

  // Reset chart data
  timeChartData.value = getEmptyChartData();
  countChartData.value = getEmptyChartData();
  resetCharts();

  // Init dataset maps and labels
  const timeDatasetMap: Record<string, number[]> = {};
  const countDatasetMap: Record<string, number[]> = {};

  const start = new Date(from);
  const end = new Date(to);
  const current = new Date(start);

  while (current <= end) {
    const day = new Date(current.toDateString());
    timeChartData.value.labels?.push(day);
    countChartData.value.labels?.push(day);

    Object.keys(timeColorMap).forEach((eventType) => {
      timeDatasetMap[eventType] = timeDatasetMap[eventType] || [];
      timeDatasetMap[eventType].push(0);
    });

    Object.keys(countColorMap).forEach((eventType) => {
      countDatasetMap[eventType] = countDatasetMap[eventType] || [];
      countDatasetMap[eventType].push(0);
    });

    current.setDate(current.getDate() + 1);
  }

  const fromEpoch = Math.floor(from.getTime() / 1000);
  const toEpoch = Math.floor(to.getTime() / 1000);

  try {
    try {
      robflowVariables.value = await loadRobotRobFlowVariables(targetCu.value);
    } catch (err) {
      console.warn('Could not load RobFlow Variables:', err);
      robflowVariables.value = null;
    }

    await Promise.all([
      streamTimeMetrics(fromEpoch, toEpoch, timeDatasetMap),
      streamCountMetrics(fromEpoch, toEpoch, countDatasetMap),
    ]);
  } catch (err) {
    console.error(`Error in metrics stream:`, err);
  } finally {
    isStreaming.value = false;
  }
}

const onTimePerformanceCardClick = (key: string) => {
  const index = Object.keys(timeColorMap).indexOf(key);
  if (index === -1) {
    return;
  }
  showOnlyTimeDataset(index);
};

const onCountPerformanceCardClick = (key: string) => {
  const index = Object.keys(countColorMap).indexOf(key);
  if (index === -1) {
    return;
  }
  showOnlyCountDataset(index);
};

const timeBreakdownEntries = computed(() => Object.entries(timeValues.value));

const hasTimeData = computed(() =>
  Object.values(timeValues.value).some((value) => value > 0),
);

const hasCountData = computed(() =>
  Object.values(countValues.value).some((value) => value > 0),
);

const hasData = computed(() => {
  return hasTimeData.value || hasCountData.value;
});

const updateDateRange = () => {
  if (!dateRange.value) {
    return;
  }
  const now = new Date();
  const [from, to] = dateRange.value;
  const windowSize = to.getTime() - from.getTime();
  dateRange.value = [new Date(now.getTime() - windowSize), now];
  onQueryChange();
};

const toggleFollowingState = () => {
  isFollowingCurrentState.value = !isFollowingCurrentState.value;
  if (isFollowingCurrentState.value) {
    updateInterval = window.setInterval(
      updateDateRange,
      FOLLOW_MODE_UPDATE_INTERVAL_MS,
    );
  } else if (updateInterval) {
    clearInterval(updateInterval);
    updateInterval = null;
  }
};

const updateUrlParams = () => {
  const params = new URLSearchParams();
  if (targetCu.value) {
    params.set('serial', targetCu.value);
  }
  if (dateRange.value) {
    params.set('from', dateRange.value[0].toISOString());
    params.set('to', dateRange.value[1].toISOString());
  }
  if (targetCount.value) {
    params.set('target', targetCount.value.toString());
    params.set('unit', targetCountUnit.value);
  }
  if (isFollowingCurrentState.value) {
    params.set('follow', 'true');
  }
  router.replace({ query: Object.fromEntries(params) });
};

const loadStateFromUrl = () => {
  const params = new URLSearchParams();
  Object.entries(route.query).forEach(([key, value]) => {
    if (typeof value === 'string') {
      params.set(key, value);
    }
  });

  const serial = params.get('serial');
  if (serial) {
    targetCu.value = serial;
  }

  const from = params.get('from');
  const to = params.get('to');
  if (from && to) {
    dateRange.value = [parseISO(from), parseISO(to)];
  }

  const target = params.get('target');
  if (target) {
    targetCount.value = Number(target);
  }

  const unit = params.get('unit');
  if (unit) {
    targetCountUnit.value = unit;
  }

  const follow = params.get('follow');
  if (follow === 'true') {
    isFollowingCurrentState.value = true;
    updateInterval = window.setInterval(
      updateDateRange,
      FOLLOW_MODE_UPDATE_INTERVAL_MS,
    );
  }

  if (serial) {
    nextTick(() => {
      onQueryChange();
    });
  }
};

watch(
  [targetCu, dateRange, targetCount, targetCountUnit, isFollowingCurrentState],
  () => {
    updateUrlParams();
  },
  { deep: true },
);

const exportMenu = ref(false);
const isExporting = ref(false);

const exportOptions = computed(() => {
  return [
    { title: $gettext('All Data (ZIP)'), value: 'all' as const },
    {
      title: $gettext('State Sequence (CSV)'),
      value: 'state_sequence' as const,
    },
    { title: $gettext('Count Events (CSV)'), value: 'count_events' as const },
  ] as const;
});

const handleExport = async (fileType: ExportFileType) => {
  if (!targetCu.value || !dateRange.value) {
    return;
  }

  const [from, to] = dateRange.value;
  if (!from || !to) {
    return;
  }

  isExporting.value = true;
  try {
    const fromEpoch = Math.floor(from.getTime() / 1000);
    const toEpoch = Math.floor(to.getTime() / 1000);
    await exportMetricsData(
      targetCu.value,
      fromEpoch,
      toEpoch,
      timezone,
      fileType,
    );
  } catch (err) {
    console.error('Export failed:', err);
    errorState.value = {
      message: $gettext('Failed to export data. Please try again.'),
      type: 'error',
    };
  } finally {
    isExporting.value = false;
    exportMenu.value = false;
  }
};

onMounted(async () => {
  await fetchUserRobotsSerialNumbers();
  loadStateFromUrl();
});

onBeforeUnmount(() => {
  if (timeUpdateTimeout) {
    clearTimeout(timeUpdateTimeout);
  }
  if (countUpdateTimeout) {
    clearTimeout(countUpdateTimeout);
  }
  if (updateInterval) {
    clearInterval(updateInterval);
  }
});
</script>

<template>
  <div class="pa-4 pa-sm-12">
    <div class="d-flex align-center justify-space-between mb-4">
      <h1>{{ $gettext('Metrics') }}</h1>
      <r-btn variant="outlined" class="ma-0" @click="showHelpDialog = true">
        <v-icon icon="mdi-information-outline" class="mr-2" />
        {{ $gettext('Metrics Help') }}
      </r-btn>
    </div>

    <!-- Help Dialog -->
    <v-dialog v-model="showHelpDialog" max-width="900" scrollable>
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <span>
            <v-icon>mdi-information-outline</v-icon>
            {{ $gettext('Metrics Help') }}
          </span>
          <v-icon size="small" @click="showHelpDialog = false"
            >mdi-close</v-icon
          >
        </v-card-title>
        <v-card-text>
          <v-tabs v-model="helpDialogTab" color="primary" class="mb-4">
            <v-tab value="time">
              <v-icon icon="mdi-clock-outline" class="mr-2" />
              {{ $gettext('Time Metrics') }}
            </v-tab>
            <v-tab value="count">
              <v-icon icon="mdi-counter" class="mr-2" />
              {{ $gettext('Count Metrics') }}
            </v-tab>
            <v-tab value="summary">
              <v-icon icon="mdi-chart-box-outline" class="mr-2" />
              {{ $gettext('Summary Metrics') }}
            </v-tab>
          </v-tabs>

          <v-window v-model="helpDialogTab">
            <!-- Time Metrics Tab -->
            <v-window-item value="time">
              <v-table class="mb-6">
                <thead>
                  <tr>
                    <th>{{ $gettext('Metric') }}</th>
                    <th>{{ $gettext('Description') }}</th>
                    <th>{{ $gettext('Trigger') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="metric in metricsHelpData.timeMetrics"
                    :key="metric.name"
                  >
                    <td class="font-weight-medium">{{ metric.name }}</td>
                    <td>{{ metric.description }}</td>
                    <td class="text-pre-line" v-html="metric.trigger" />
                  </tr>
                </tbody>
              </v-table>
            </v-window-item>

            <!-- Count Metrics Tab -->
            <v-window-item value="count">
              <v-table class="mb-6">
                <thead>
                  <tr>
                    <th>{{ $gettext('Metric') }}</th>
                    <th>{{ $gettext('Description') }}</th>
                    <th>{{ $gettext('Trigger') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="metric in metricsHelpData.countMetrics"
                    :key="metric.name"
                  >
                    <td class="font-weight-medium">{{ metric.name }}</td>
                    <td>{{ metric.description }}</td>
                    <td class="text-pre-line" v-html="metric.trigger" />
                  </tr>
                </tbody>
              </v-table>
            </v-window-item>

            <!-- Summary Metrics Tab -->
            <v-window-item value="summary">
              <v-table>
                <thead>
                  <tr>
                    <th>{{ $gettext('Metric') }}</th>
                    <th>{{ $gettext('Description') }}</th>
                    <th>{{ $gettext('Calculation') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="metric in metricsHelpData.summaryMetrics"
                    :key="metric.name"
                  >
                    <td class="font-weight-medium">{{ metric.name }}</td>
                    <td>{{ metric.description }}</td>
                    <td>{{ metric.calculation }}</td>
                  </tr>
                </tbody>
              </v-table>
            </v-window-item>
          </v-window>
        </v-card-text>
      </v-card>
    </v-dialog>

    <div class="mb-4">
      <v-autocomplete
        v-model="targetCu"
        :items="serialNumbers"
        :label="$gettext('Serial number')"
        :rules="[requiredRule, serialNumberRegexRule]"
        style="max-width: 300px"
        @update:modelValue="onQueryChange"
      />
    </div>

    <v-row>
      <v-col cols="12" md="6" lg="4">
        <DatePickerComponent
          v-model:dateRange="dateRange"
          class="mt-2"
          @update:dateRange="onQueryChange"
        />
      </v-col>
      <v-col>
        <div :class="{ 'float-end': mdAndUp }">
          <r-btn
            variant="outlined"
            :disabled="!targetCu || isStreaming"
            class="mr-2 mt-2"
            :color="isFollowingCurrentState ? 'primary' : undefined"
            @click="toggleFollowingState"
          >
            <v-icon
              :icon="
                isFollowingCurrentState
                  ? 'mdi-clock-check'
                  : 'mdi-clock-outline'
              "
              class="mr-2"
            />
            {{
              isFollowingCurrentState
                ? $gettext('Automatic refresh activated')
                : $gettext('Start Automatic refresh')
            }}
          </r-btn>
          <r-btn
            variant="outlined"
            :disabled="!targetCu || isStreaming"
            class="mr-2 mt-2"
            @click="onQueryChange"
          >
            <v-icon icon="mdi-refresh" class="mr-2" />
            {{ $gettext('Refresh') }}
          </r-btn>
          <r-btn
            variant="outlined"
            :disabled="!timeBarChart?.chart || isStreaming"
            class="mr-2 mt-2"
            @click="resetCharts"
          >
            <v-icon icon="mdi-chart-timeline-variant-shimmer" class="mr-2" />
            {{ $gettext('Reset Chart View') }}
          </r-btn>
          <v-menu v-model="exportMenu" :close-on-content-click="false">
            <template v-slot:activator="{ props }">
              <r-btn
                variant="outlined"
                v-bind="props"
                :disabled="!targetCu || isStreaming || isExporting"
                class="mr-4 mt-2"
              >
                <v-icon icon="mdi-download" class="mr-2" />
                {{ $gettext('Export') }}
                <v-icon icon="mdi-chevron-down" class="ml-2" />
              </r-btn>
            </template>
            <v-list>
              <v-list-item
                v-for="option in exportOptions"
                :key="option.value"
                :disabled="isExporting"
                @click="handleExport(option.value)"
              >
                <template v-slot:prepend>
                  <v-icon
                    :icon="
                      option.value === 'all'
                        ? 'mdi-zip-box'
                        : 'mdi-file-delimited'
                    "
                  />
                </template>
                <v-list-item-title>
                  {{ option.title }}
                  <v-progress-circular
                    v-if="isExporting"
                    indeterminate
                    size="16"
                    width="2"
                    class="ml-2"
                  />
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </v-col>
    </v-row>

    <v-alert
      v-if="errorState.message"
      :type="errorState.type || 'error'"
      :icon="errorState.type === 'warning' ? 'mdi-alert' : 'mdi-alert-circle'"
      class="mb-4"
      closable
      @click:close="clearError"
    >
      {{ errorState.message }}
    </v-alert>

    <!-- Empty state messages -->
    <div
      v-if="!targetCu"
      class="empty-state d-flex flex-column align-center justify-center"
    >
      <v-icon
        icon="mdi-chart-timeline-variant"
        size="64"
        class="mb-4 text-grey-lighten-1"
      />
      <h3 class="text-h6 text-grey-darken-1 text-center mb-2">
        {{ $gettext('Select a serial number to view metrics') }}
      </h3>
    </div>
    <div
      v-else-if="targetCu && !isStreaming && !hasData"
      class="empty-state d-flex flex-column align-center justify-center"
    >
      <v-icon
        icon="mdi-chart-timeline-variant"
        size="64"
        class="mb-4 text-grey-lighten-1"
      />
      <h3 class="text-h6 text-grey-darken-1 text-center mb-2">
        {{ $gettext('No data available for the selected period') }}
      </h3>
      <p class="text-body-2 text-grey-darken-1 text-center">
        {{ $gettext('Try selecting a different date range.') }}
      </p>
      <p class="text-body-2 text-grey-darken-1 text-center">
        {{
          $gettext(
            'Update your Robot to the latest software version (V6.0 or later) and connect it to the internet for enabling OEE metrics',
          )
        }}
      </p>
    </div>

    <!-- Charts and metrics -->
    <template v-else-if="targetCu && (isStreaming || hasData)">
      <v-row class="mb-8" align="stretch">
        <!-- Time Metrics Chart Column -->
        <v-col cols="12" lg="8" class="d-flex flex-column pa-0 justify-center">
          <h2 class="text-h6 mb-2 ml-3">{{ $gettext('Time Metrics') }}</h2>
          <v-alert
            v-if="!hasTimeData && !isStreaming"
            type="info"
            variant="tonal"
            class="mx-3 mb-2"
          >
            <div class="font-weight-medium mb-1">
              {{ $gettext('Missing metrics?') }}
            </div>
            <div class="text-body-2">
              {{
                $gettext(
                  'Update your Robot to the latest software version (V6.0 or later) and connect it to the internet for enabling OEE metrics',
                )
              }}
            </div>
          </v-alert>
          <div class="chart-wrapper">
            <div class="chart-container">
              <Bar
                ref="timeBarChart"
                :data="timeChartData"
                :options="timeChartOptions"
              />
              <div v-if="isStreaming" class="loading-overlay">
                <v-progress-circular indeterminate color="primary" size="32" />
              </div>
            </div>
          </div>
        </v-col>

        <!-- Time Performance Breakdown Column -->
        <v-col cols="12" lg="4" class="d-flex flex-column">
          <h2 class="text-h6 mb-2">
            {{ $gettext('Time Performance Breakdown') }}
          </h2>
          <div class="performance-breakdown-wrapper">
            <v-row dense>
              <v-col
                v-for="[key, value] in timeBreakdownEntries"
                :key="key"
                cols="12"
                class="pa-1"
              >
                <v-card
                  class="pa-3 performance-card"
                  :style="{
                    borderLeftColor:
                      timeColorMap[key as keyof typeof timeColorMap] ||
                      '#cccccc',
                    backgroundColor: isTimeMetricSelected(key as string)
                      ? `${timeColorMap[key as keyof typeof timeColorMap]}20` ||
                        'rgba(0, 0, 0, 0.05)'
                      : 'transparent',
                  }"
                  :class="{
                    'performance-card--selected': isTimeMetricSelected(
                      key as string,
                    ),
                  }"
                  outlined
                  @click="onTimePerformanceCardClick(key as string)"
                >
                  <div class="d-flex align-center">
                    <div class="text-subtitle-2 font-weight-medium">
                      {{ timeLabelDisplayMap[key] || key }}
                    </div>
                  </div>
                  <div class="d-flex align-center justify-space-between">
                    <v-skeleton-loader
                      :loading="isStreaming"
                      type="text"
                      width="80"
                    >
                      <span>
                        {{
                          value >= 3600
                            ? (value / 3600).toFixed(2) + ' h'
                            : Math.round(value / 60) + ' min'
                        }}
                      </span>
                    </v-skeleton-loader>
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </div>
        </v-col>
      </v-row>

      <!-- Count Metrics Row -->
      <v-row class="mb-8" align="stretch">
        <!-- Count Metrics Chart Column -->
        <v-col cols="12" lg="8" class="d-flex flex-column pa-0 justify-center">
          <h2 class="text-h6 mb-2 ml-3">{{ $gettext('Count Metrics') }}</h2>
          <v-alert
            v-if="!hasCountData && !isStreaming"
            type="info"
            variant="tonal"
            class="mx-3 mb-2"
          >
            <div class="font-weight-medium mb-1">
              {{ $gettext('Missing metrics?') }}
            </div>
            <div class="text-body-2">
              {{
                $gettext(
                  'To enable count metrics make use of the Event Node in RobFlow',
                )
              }}
            </div>
          </v-alert>
          <div class="chart-wrapper">
            <div class="chart-container">
              <Bar
                ref="countBarChart"
                :data="countChartData"
                :options="countChartOptions"
              />
              <div v-if="isStreaming" class="loading-overlay">
                <v-progress-circular indeterminate color="primary" size="32" />
              </div>
            </div>
          </div>
        </v-col>

        <!-- Count Performance Breakdown Column -->
        <v-col cols="12" lg="4" class="d-flex flex-column">
          <h2 class="text-h6 mb-2">
            {{ $gettext('Count Performance Breakdown') }}
          </h2>
          <div class="performance-breakdown-wrapper">
            <v-row dense>
              <v-col
                v-for="(value, key) in countValues"
                :key="key"
                cols="12"
                class="pa-1"
              >
                <v-card
                  class="pa-3 performance-card"
                  :style="{
                    borderLeftColor:
                      countColorMap[key as keyof typeof countColorMap] ||
                      '#cccccc',
                    backgroundColor: selectedCountMetrics.has(key)
                      ? `${countColorMap[key as keyof typeof countColorMap]}20` ||
                        'rgba(0, 0, 0, 0.05)'
                      : 'transparent',
                  }"
                  :class="{
                    'performance-card--selected': selectedCountMetrics.has(key),
                  }"
                  outlined
                  @click="onCountPerformanceCardClick(key as string)"
                >
                  <div class="d-flex align-center">
                    <div class="text-subtitle-2 font-weight-medium">
                      {{ countLabelDisplayMap[key] || key }}
                    </div>
                  </div>
                  <div class="d-flex align-center justify-space-between">
                    <v-skeleton-loader
                      :loading="isStreaming"
                      type="text"
                      width="80"
                    >
                      {{ value }}
                    </v-skeleton-loader>
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </div>
        </v-col>
      </v-row>

      <!-- Summary Metrics -->
      <v-row class="mt-4">
        <v-col cols="12" class="mb-2">
          <h2 class="text-h6 mb-2">{{ $gettext('Summary Metrics') }}</h2>
        </v-col>
        <v-col cols="12" class="mb-4">
          <div class="d-flex align-center gap-2" style="max-width: 400px">
            <v-text-field
              v-model="targetCount"
              type="number"
              :label="$gettext('Target Count')"
              min="0"
              density="comfortable"
              variant="outlined"
              hide-details
              class="flex-grow-1"
            />
            <v-select
              v-model="targetCountUnit"
              :items="targetCountUnits"
              density="comfortable"
              variant="outlined"
              hide-details
              style="width: 150px"
            />
          </div>
        </v-col>
        <!-- OEE Metric -->
        <v-col cols="12" class="mb-4">
          <v-card class="pa-4 highlight-metric">
            <div class="d-flex align-center">
              <div class="text-h6 font-weight-medium mb-2">
                {{ $gettext('OEE') }}
              </div>
            </div>
            <div class="d-flex align-center">
              <v-skeleton-loader :loading="isStreaming" type="text" width="80">
                {{ (oee * 100).toFixed(2) }}%
              </v-skeleton-loader>
            </div>
          </v-card>
        </v-col>
        <!-- Other Metrics -->
        <v-col
          v-for="metric in summaryMetrics"
          :key="metric.label"
          cols="12"
          sm="6"
          md="4"
        >
          <v-card class="pa-3" outlined>
            <div class="d-flex align-center">
              <div class="text-subtitle-2 font-weight-medium">
                {{ metric.label }}
              </div>
            </div>
            <div class="d-flex align-center justify-space-between">
              <v-skeleton-loader :loading="isStreaming" type="text" width="80">
                {{ metric.value }}
              </v-skeleton-loader>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <!-- RobFlow Variables: always show when a serial number is selected, placed after Summary -->
    <v-row v-if="targetCu" class="mt-2">
      <v-col cols="12" class="mt-8">
        <h2 class="text-h6">{{ $gettext('RobFlow Variables') }}</h2>
      </v-col>
      <v-col cols="12">
        <div
          v-if="
            robflowVariables &&
            Object.keys(robflowVariables.variables || {}).length > 0
          "
        >
          <p class="text-body-2 text-muted mb-2">
            {{ $gettext('Last update') }}:
            {{ formatDate((robflowVariables.timestamp || 0) * 1000, true) }}
          </p>
          <v-data-table
            class="my-2"
            :headers="robflowHeaders"
            :items="robflowTableData"
            density="compact"
          />
        </div>
        <v-alert v-else type="info" variant="tonal">
          <div class="mb-2">
            {{
              $gettext(
                'No Variables from RobFlow are currently synced for this Control Unit',
              )
            }}
          </div>
          <div class="text-body-2">
            {{
              $gettext(
                'To sync Variables from RobFlow, open RobFlow and enable Cloud Sync in the Variable Settings',
              )
            }}
          </div>
        </v-alert>
      </v-col>
    </v-row>
  </div>
</template>

<style scoped lang="scss">
.text-muted {
  color: #999;
}

.chart-wrapper {
  width: 100%;
}

.chart-container {
  position: relative;
  width: 100%;
  aspect-ratio: 2 / 1;
  min-width: 0;

  :deep(canvas) {
    position: absolute;
    top: 0;
    left: 0;
  }
}

.loading-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

.performance-breakdown-wrapper {
  width: 100%;
  padding: 0 16px;
}

.v-skeleton-loader {
  background: transparent !important;
  :deep(.v-skeleton-loader__text) {
    height: 24px !important;
    margin: 0 !important;
    min-height: 24px !important;
    transform: none !important;
  }
}

.performance-card {
  border-left-width: 4px !important;
  border-left-style: solid !important;
  transition: all 0.3s ease;
  cursor: pointer;
  width: 100%;
  margin-bottom: 8px;

  &:hover {
    transform: translateX(4px);
    background-color: rgba(0, 0, 0, 0.03);
  }

  &--selected {
    transform: translateX(4px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    &:hover {
      transform: translateX(6px);
    }
  }
}

.empty-state {
  min-height: 400px;
  border-radius: 8px;
  border: 2px dashed rgba(0, 0, 0, 0.12);
  margin: 16px 0;
  padding: 24px;
}

.highlight-metric {
  border: 2px solid var(--v-primary-base);
  background-color: var(--v-primary-lighten-5);
}

.gap-2 {
  gap: 8px;
}

.v-tabs {
  :deep(.v-tab) {
    text-transform: none;
    letter-spacing: normal;
  }
}
</style>
